import type { ChangeEvent } from 'react';
import type { AuthUser } from '@/types/auth';
import { useMyTenants } from '@/features/auth/useMyTenants';
import { useSwitchTenant } from '@/features/auth/useSwitchTenant';

interface HeaderProps {
  onLogout: () => void;
  user?: AuthUser;
  onToggleSidebar: () => void;
}

/**
 * Top bar — displays the tenant switcher (CBS operators with ≥2 tenants),
 * the current user, and a logout button. Offset from the left by the sidebar
 * width (240px).
 *
 * TODO: route switchError through a shared toast primitive once one exists
 * under components/common/. For now we render the error inline below the
 * select in red.
 */
export function Header({ onLogout, user, onToggleSidebar }: HeaderProps) {
  const { tenants } = useMyTenants();
  const { switchTo, isSwitching, error: switchError } = useSwitchTenant();

  const handleTenantChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const nextCod = e.target.value;
    if (!nextCod || nextCod === user?.tenant_cod) return;
    try {
      await switchTo(nextCod);
    } catch {
      // error already captured by hook
    }
  };

  const showSwitcher = !!user && user.role === 'cbs_admin' && tenants.length > 1;
  const showStaticLabel = !!user && user.role === 'cbs_admin' && tenants.length === 1;

  return (
    <header className="fixed top-0 right-0 left-0 md:left-[240px] z-50 flex h-14 items-center justify-between border-b border-[#e8e8e8] bg-white px-6">
      <button
        type="button"
        onClick={onToggleSidebar}
        className="rounded p-1.5 text-[#666] transition-colors hover:bg-[#f0f0f0] hover:text-[#333] md:hidden"
        aria-label="Toggle sidebar navigation"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <div className="flex-1" />

      <div className="flex items-center gap-4">
        {showSwitcher && (
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              <label htmlFor="headerTenantSelect" className="sr-only">
                Tenant
              </label>
              <select
                id="headerTenantSelect"
                value={user.tenant_cod}
                onChange={handleTenantChange}
                disabled={isSwitching}
                aria-label="Switch tenant"
                className="rounded-md border border-[#ddd] bg-white px-2.5 py-1.5 text-[12px] text-[#333] transition-colors hover:bg-[#f7f7f7] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {tenants.map((t) => (
                  <option key={t.tenant_cod} value={t.tenant_cod}>
                    {t.tenant_lib} ({t.tenant_cod})
                  </option>
                ))}
              </select>
              {isSwitching && (
                <span className="text-[11px] text-[#666]" aria-live="polite">
                  Switching…
                </span>
              )}
            </div>
            {switchError && (
              <p role="alert" className="mt-1 text-[11px]" style={{ color: '#b00020' }}>
                {switchError}
              </p>
            )}
          </div>
        )}
        {showStaticLabel && (
          <span
            className="rounded-md bg-[#f5f5f5] px-2.5 py-1 text-[11px] font-semibold text-[#555]"
            title={`Current tenant: ${tenants[0].tenant_lib}`}
          >
            {tenants[0].tenant_lib}
          </span>
        )}
        {user && <span className="text-[13px] font-semibold text-[#333]">{user.username}</span>}
        <button
          type="button"
          onClick={onLogout}
          className="rounded-md border border-[#ddd] bg-white px-3.5 py-1.5 text-[11px] text-[#666] transition-colors hover:bg-[#f0f0f0]"
          aria-label="Log out"
        >
          Log Out
        </button>
      </div>
    </header>
  );
}
